/**
 * Personal desk laptop bookmarks — optional local extras.
 * Workspace systems come from the admin catalog (server); personal tiles stay browser-local.
 */

import { z } from "zod";

export const laptopBookmarkSchema = z.object({
  id: z.string().min(1).max(64),
  label: z.string().trim().min(1).max(40),
  url: z
    .string()
    .trim()
    .max(2000)
    .refine((u) => {
      try {
        const parsed = new URL(u);
        return parsed.protocol === "https:" || parsed.protocol === "http:";
      } catch {
        return false;
      }
    }, "URL must be http(s)"),
  /** Optional brand accent for the app tile */
  color: z
    .string()
    .regex(/^#[0-9A-Fa-f]{6}$/)
    .optional(),
});

export type LaptopBookmark = z.infer<typeof laptopBookmarkSchema>;

export const laptopBookmarksListSchema = z.array(laptopBookmarkSchema).max(24);

const STORAGE_KEY = "nymbl_vyllage_laptop_bookmarks_v2";
const LEGACY_STORAGE_KEY = "nymbl_vyllage_laptop_bookmarks_v1";

/** Ids from the pre-admin default set (v1) — strip on migrate so they don't duplicate workspace systems. */
const LEGACY_DEFAULT_IDS = new Set([
  "slack",
  "gmail",
  "calendar",
  "drive",
  "github",
  "notion",
  "figma",
  "linear",
]);

/**
 * Personal list starts empty — workspace systems come from the admin catalog.
 * User-added personal tiles are optional local extras.
 */
export const DEFAULT_LAPTOP_BOOKMARKS: readonly LaptopBookmark[] = [];

export function newBookmarkId(): string {
  return crypto.randomUUID();
}

/** Normalize URL: add https:// if user typed a host only. */
export function normalizeBookmarkUrl(raw: string): string {
  const t = raw.trim();
  if (!t) return t;
  if (/^https?:\/\//i.test(t)) return t;
  return `https://${t}`;
}

function parseList(raw: string): LaptopBookmark[] | null {
  try {
    const parsed: unknown = JSON.parse(raw);
    const result = laptopBookmarksListSchema.safeParse(parsed);
    if (!result.success) return null;
    return result.data;
  } catch {
    return null;
  }
}

export function loadLaptopBookmarks(): LaptopBookmark[] {
  try {
    const v2 = localStorage.getItem(STORAGE_KEY);
    if (v2) {
      return parseList(v2) ?? [];
    }
    // One-time migrate from v1: drop the old starter defaults (now workspace systems).
    const v1 = localStorage.getItem(LEGACY_STORAGE_KEY);
    if (v1) {
      const list = parseList(v1) ?? [];
      const personal = list.filter((b) => !LEGACY_DEFAULT_IDS.has(b.id));
      saveLaptopBookmarks(personal);
      try {
        localStorage.removeItem(LEGACY_STORAGE_KEY);
      } catch {
        // ignore
      }
      return personal;
    }
    return [];
  } catch {
    return [];
  }
}

export function saveLaptopBookmarks(bookmarks: LaptopBookmark[]): void {
  try {
    const cleaned = laptopBookmarksListSchema.parse(bookmarks);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(cleaned));
  } catch {
    // Quota / private mode — fail silent; UI still works for the session
  }
}

export function resetLaptopBookmarks(): LaptopBookmark[] {
  const next: LaptopBookmark[] = [];
  saveLaptopBookmarks(next);
  return next;
}

/** Two-letter tile label from app name. */
export function bookmarkInitials(label: string): string {
  const parts = label.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) {
    const w = parts[0] ?? "";
    return w.slice(0, 2).toUpperCase();
  }
  const a = parts[0]?.[0] ?? "";
  const b = parts[1]?.[0] ?? "";
  return `${a}${b}`.toUpperCase();
}
